import { useEffect, useState, useCallback, useMemo, memo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusChip, { TriStateChip } from "@/components/StatusChip";
import PhotoStrip from "@/components/PhotoStrip";
import { downloadInspectionPhotosZip } from "@/lib/photoBackup";
import { resolveAllLazyPhotos } from "@/lib/lazyPhotos";
import EmailReportDialog from "@/components/EmailReportDialog";
import EmailHistory from "@/components/EmailHistory";
import DocumentsBillingCard from "@/components/billing/DocumentsBillingCard";
import NoticeDialog from "@/components/NoticeDialog";
import SignaturePad from "@/components/SignaturePad";
import ReportEditor from "@/components/ReportEditor";
import AddressField from "@/components/AddressField";
import AIPhotoCheck from "@/components/AIPhotoCheck";
import VoiceInspection from "@/components/VoiceInspection";
import VoiceToNotes from "@/components/VoiceToNotes";
import CustomChecklistRenderer from "@/components/CustomChecklistRenderer";
import MarkdownLite from "@/components/MarkdownLite";
import UpgradePrompt from "@/components/UpgradePrompt";
import { isVisible } from "@/lib/checklist";
import { queueInspectionSave, clearInspectionFromQueue, isNetworkError } from "@/lib/offlineQueue";
import { slimPayload } from "@/lib/photoOffload";
import { kvSet, readLocal, removeLocal, draftKey, recoveryKey } from "@/lib/localStore";
import {
  cacheSchema, getCachedSchema, getCachedInspectionsList,
  isLocalId, getLocalInspection, updateLocalInspection, getLocalIdMap,
} from "@/lib/offlineCache";
import { useLeaveGuard, useOverlayBackClose } from "@/hooks/backGuard";
import { snapshotInspection, getCachedInspection, getCachedPdf } from "@/lib/offlineReports";
import { toInput, fromInput } from "@/lib/datetime";
import {
  ArrowLeft, Save, Loader2, Sparkles, Download, Trash2, ImageDown,
  FileText, ChevronDown, Mail, Flag, FileWarning, Image as ImageIcon, Lock, Unlock, CheckCircle2,
  History, Share2, Copy as CopyIcon, X as XIcon, Lock as LockIcon, AlertTriangle, Camera,
  CloudUpload, CloudOff, RefreshCw, Cloud, MoreHorizontal, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

// Full offline copy of an inspection (incl. photos), refreshed on each online
// load. The slim dashboard list no longer carries photos, so this is what lets
// an already-opened inspection work fully offline.
const fullCacheKey = (id) => `psc_insp_full_${id}`;

const InspectionDetail = () => {
  const { id } = useParams();
  // Phone back button: confirm before leaving a live checklist.
  useLeaveGuard("Leave this inspection checklist? Your work is saved as a draft on this device.");
  const navigate = useNavigate();
  const { user } = useAuth();
  // Master admin bypasses tier gates server-side; for everyone else, only Pro+
  // can use AI reports + Customer Portal. Free tier sees a lock badge as a hint.
  const isPro = user?.role === "admin" || ["solo", "pro", "enterprise"].includes(user?.tier);

  const [schema, setSchema] = useState(null);
  const [insp, setInsp] = useState(null);
  const [offlineMode, setOfflineMode] = useState(false);
  const [offlineDraft, setOfflineDraft] = useState(false);
  const [photosPartial, setPhotosPartial] = useState(false); // editing from a photo-stripped offline copy
  // Stats for inspections using a custom template_snapshot — populated by CustomChecklistRenderer.
  const [customStats, setCustomStats] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const usingCustomTpl = !!insp?.template_snapshot;
  const [title, setTitle] = useState("");
  const [items, setItems] = useState({});
  const [manualOutcome, setManualOutcome] = useState(null);          // "compliant" | "non_compliant" | null
  const [manualOutcomeReason, setManualOutcomeReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Autosave state — mirrors the checklist-builder pattern
  const [autoSaveState, setAutoSaveState] = useState("idle"); // idle | dirty | saving | saved | offline | retrying | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [, forceTick] = useState(0);
  // Expose unsaved-work state to the service-worker updater so it never
  // reloads (and wipes input) while an inspection has pending changes.
  useEffect(() => {
    const pending = ["dirty", "saving", "offline", "retrying", "error"];
    window.__pscDirty = pending.includes(autoSaveState);
    return () => { window.__pscDirty = false; };
  }, [autoSaveState]);

  const [genLoading, setGenLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [correctStatusOpen, setCorrectStatusOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [reportOpen, setReportOpen] = useState(true);
  const [upgrade, setUpgrade] = useState({ open: false, feature: "this feature", message: "" });

  const locked = !!insp?.locked;

  useEffect(() => {
    (async () => {
      // Schema: network first, IndexedDB cache as the offline fallback.
      let sections = null;
      try {
        const s = await api.get("/checklist/schema");
        sections = s.data.sections;
        cacheSchema(sections);
      } catch {
        sections = await getCachedSchema();
      }
      if (sections) setSchema(sections);

      // Offline-created inspection (hasn't reached the server yet)?
      if (isLocalId(id)) {
        const map = await getLocalIdMap();
        if (map[id]) { navigate(`/inspections/${map[id]}`, { replace: true }); return; }
        const local = await getLocalInspection(id);
        if (!local) { toast.error("Inspection not found"); navigate("/"); return; }
        setInsp(local);
        setTitle(local.title || "");
        setItems(local.items || {});
        setManualOutcome(local.manual_outcome || null);
        setManualOutcomeReason(local.manual_outcome_reason || "");
        setOfflineDraft(true);
        setLoading(false);
        return;
      }

      try {
        const r = await api.get(`/inspections/${id}?lazy=1`);
        setInsp(r.data);

        // Merge order: (1) server copy → (2) local draft (if newer than server)
        //               → (3) 401-recovery stash (only if it holds real data).
        // The draft is written on EVERY change (see the draft effect below) so
        // a hard-close never loses in-memory work.
        const serverItems = r.data.items || {};
        const serverUpdatedAt = new Date(r.data.updated_at || 0).getTime();

        // Local continuous draft (written on every state change) — IndexedDB,
        // with legacy localStorage fallback.
        const draft = await readLocal(draftKey(id));

        // 401-recovery snapshot (written only when a save 401'd)
        const recWrap = await readLocal(recoveryKey(id));
        const recovered = recWrap?.payload || null;

        const isRealItems = (obj) => obj && typeof obj === "object" && Object.keys(obj).length > 0;
        const hasContent = (payload) =>
          payload && (
            isRealItems(payload.items)
            || (payload.title && payload.title.trim())
            || (payload.manual_outcome && String(payload.manual_outcome).trim())
          );

        // Pick the freshest source of truth.
        // Priority: any local source that has real content AND is newer than
        // the server's updated_at wins. Otherwise use the server copy.
        const draftAt = draft?.savedAt || 0;
        const recoveredAt = recWrap?.savedAt || 0;
        // A local copy wins when it is newer than the server OR when it holds
        // MORE answered items — an emptier server copy must never beat a
        // richer local draft (root cause of the Macksville data loss).
        const answeredIn = (payload) => Object.values(payload?.items || {}).filter(
          (v) => v && ((v.answer && String(v.answer).trim()) || (v.notes && v.notes.trim()) || (v.photos || []).length > 0)
        ).length;
        const serverAnswered = answeredIn({ items: serverItems });
        const draftFresher = hasContent(draft?.payload)
          && (draftAt > serverUpdatedAt || answeredIn(draft.payload) > serverAnswered);
        const recoveredFresher = hasContent(recovered)
          && (recoveredAt > serverUpdatedAt || answeredIn(recovered) > serverAnswered);

        let source = r.data;                        // default = server
        let restoredFromLocal = false;
        if (draftFresher && draftAt >= recoveredAt) {
          source = { ...r.data, ...draft.payload };
          restoredFromLocal = true;
          const extra = answeredIn(draft.payload) - serverAnswered;
          if (extra > 0) toast.success(`Recovered ${extra} answer${extra > 1 ? "s" : ""} from this device that hadn't reached the server`);
        } else if (recoveredFresher) {
          source = { ...r.data, ...recovered };
          restoredFromLocal = true;
          toast.success("Restored your unsaved work from before the sign-in");
        }

        setTitle(source.title || "");
        setItems(isRealItems(source.items) ? source.items : serverItems);
        setManualOutcome(source.manual_outcome || null);
        setManualOutcomeReason(source.manual_outcome_reason || "");

        if (restoredFromLocal) {
          // Local draft was fresher — nothing else to do.
        } else {
          // Server was authoritative — clear any stale local artefacts so
          // future loads don't accidentally restore old empty snapshots.
          removeLocal(draftKey(id));
          removeLocal(recoveryKey(id));
        }

        // Refresh the offline caches in the background with the FULL photo
        // payload — the interactive load above is lazy for instant open.
        api.get(`/inspections/${id}`).then((full) => {
          if (full.data.ai_report_finalized) snapshotInspection(full.data);
          try { kvSet(fullCacheKey(id), { payload: full.data, savedAt: Date.now() }); } catch { /* best-effort */ }
        }).catch(() => { /* offline caches keep their previous copy */ });
      } catch (err) {
        // Fallback chain (offline / dead zone):
        //   1. full cached copy from a previous online open (EDITABLE, has photos)
        //   2. cached finalised snapshot (read-only)
        //   3. cached dashboard-list copy + local draft (EDITABLE — photos stripped)
        const fullWrap = await readLocal(fullCacheKey(id));
        const cached = getCachedInspection(id);
        if (fullWrap?.payload) {
          const base = fullWrap.payload;
          const draft = await readLocal(draftKey(id));
          const useDraft = draft?.payload && Object.keys(draft.payload.items || {}).length > 0
            && (draft.savedAt || 0) >= (fullWrap.savedAt || 0);
          const src = useDraft ? { ...base, ...draft.payload } : base;
          setInsp(base);
          setTitle(src.title || "");
          setItems(src.items || {});
          setManualOutcome(src.manual_outcome || null);
          setManualOutcomeReason(src.manual_outcome_reason || "");
          setOfflineDraft(true);
          toast.info("Offline — working from the copy on this device. Changes sync automatically.");
        } else if (cached) {
          setInsp({ ...cached, items: cached.items || {} });
          setTitle(cached.title || "");
          setItems(cached.items || {});
          setManualOutcome(cached.manual_outcome || null);
          setManualOutcomeReason(cached.manual_outcome_reason || "");
          setOfflineMode(true);
          toast.info("Offline — showing the cached finalised report");
        } else {
          const draft = await readLocal(draftKey(id));
          const listWrap = await getCachedInspectionsList();
          const entry = (listWrap?.list || []).find((x) => x.id === id);
          const draftHasContent = draft?.payload && Object.keys(draft.payload.items || {}).length > 0;
          if (entry || draftHasContent) {
            const base = entry || { id, title: "", items: {} };
            const useDraft = draftHasContent && (draft.savedAt || 0) >= new Date(base.updated_at || 0).getTime();
            const src = useDraft ? { ...base, ...draft.payload } : base;
            // The slim list cache has no photos — flag saves so the server
            // preserves existing photos instead of wiping them on sync.
            if (base.photos_stripped) setPhotosPartial(true);
            setInsp(base);
            setTitle(src.title || "");
            setItems(src.items || {});
            setManualOutcome(src.manual_outcome || null);
            setManualOutcomeReason(src.manual_outcome_reason || "");
            setOfflineDraft(true);
            toast.info("Offline — working from the copy on this device. Changes sync automatically.");
          } else {
            toast.error("Inspection not found");
            navigate("/");
          }
        }
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const updateItem = useCallback((itemId, patch) => {
    setItems((prev) => {
      const current = prev[itemId] || { answer: "", notes: "", photos: [] };
      return { ...prev, [itemId]: { ...current, ...patch } };
    });
  }, []);

  const save = useCallback(async (showToast = true) => {
    setSaving(true);
    setAutoSaveState("saving");
    let payload = {
      title,
      items,
      manual_outcome: manualOutcome,
      manual_outcome_reason: manualOutcomeReason,
      ...(photosPartial ? { photos_partial: true } : {}),
    };
    // Ship large photos one-by-one first so the save itself stays small
    // (big PUT bodies get cut by the proxy and look like network errors).
    if (navigator.onLine && !isLocalId(id)) {
      try { payload = await slimPayload(payload, id); } catch { /* keep inline */ }
    }
    // Offline-created inspection — no server id yet. Save on-device; the
    // offline queue POSTs it (and maps local → real id) when back online.
    if (isLocalId(id)) {
      try {
        const map = await getLocalIdMap();
        if (map[id]) {
          // background sync created it server-side while we were editing
          await api.put(`/inspections/${map[id]}`, payload);
          navigate(`/inspections/${map[id]}`, { replace: true });
          return;
        }
      } catch { /* still offline — fall through to the device copy */ }
      await updateLocalInspection(id, payload);
      setLastSavedAt(Date.now());
      setAutoSaveState(navigator.onLine ? "saved" : "offline");
      setSaving(false);
      if (showToast) toast.success("Saved on this device — will sync when online");
      return;
    }
    try {
      const r = await api.put(`/inspections/${id}`, payload);
      setInsp(r.data);
      clearInspectionFromQueue(id);
      // Clear the recovery + draft snapshots — data is now safe on the server
      removeLocal(recoveryKey(id));
      removeLocal(draftKey(id));
      setLastSavedAt(Date.now());
      setAutoSaveState("saved");
      if (showToast) toast.success("Saved");
    } catch (e) {
      if (isNetworkError(e)) {
        queueInspectionSave(id, payload);
        setAutoSaveState("offline");
        setLastSavedAt(Date.now());
        if (showToast) toast.success("Saved offline — will sync when online");
      } else if (e?.response?.status === 423) {
        setAutoSaveState("error");
        toast.error("Inspection is locked — unlock it before editing");
      } else if (e?.response?.status === 401) {
        // Session expired — stash current draft so we can restore after login.
        kvSet(recoveryKey(id), { payload, savedAt: Date.now() });
        setAutoSaveState("error");
        toast.error("Session expired — please sign in again. Your work is saved.");
      } else if (e?.response?.status === 402) {
        setAutoSaveState("error"); // read-only mode — interceptor already toasts why
      } else {
        setAutoSaveState("retrying");
        if (showToast) toast.error("Save failed — will retry automatically");
      }
    } finally { setSaving(false); }
  }, [id, title, items, manualOutcome, manualOutcomeReason, photosPartial]);

  // ---- Autosave: debounce saves ~1.5s after any change ----
  // Skips the first render (which is just loading server data) so we never
  // fire a spurious empty save on open.
  const autoSaveSkip = useMemo(() => ({ current: true }), []);
  useEffect(() => {
    if (loading || locked) return;
    if (autoSaveSkip.current) { autoSaveSkip.current = false; return; }
    setAutoSaveState("dirty");
    // 1) Snapshot a local draft to IndexedDB shortly after typing pauses.
    //    (Was synchronous on every keystroke — serialising photo-heavy
    //    inspections froze the UI when editing/deleting long notes.)
    // Guard: never overwrite a real existing draft with an empty items dict.
    let draftTimer;
    if (Object.keys(items || {}).length > 0 || (title && title.trim()) || manualOutcome) {
      draftTimer = setTimeout(() => {
        kvSet(draftKey(id), {
          payload: {
            title,
            items,
            manual_outcome: manualOutcome,
            manual_outcome_reason: manualOutcomeReason,
            ...(photosPartial ? { photos_partial: true } : {}),
          },
          savedAt: Date.now(),
        });
      }, 600);
    }
    // 2) Fire the server save after debounce.
    const timer = setTimeout(() => { save(false); }, 1500);
    return () => { clearTimeout(timer); if (draftTimer) clearTimeout(draftTimer); };
  }, [id, title, items, manualOutcome, manualOutcomeReason, loading, locked, save]);

  // Guardian work-protection: snapshot the draft instantly when the AI help
  // widget is about to attempt a fix.
  useEffect(() => {
    const onCheckpoint = () => {
      try {
        kvSet(draftKey(id), {
          payload: { title, items, manual_outcome: manualOutcome,
                     manual_outcome_reason: manualOutcomeReason,
                     ...(photosPartial ? { photos_partial: true } : {}) },
          savedAt: Date.now(),
        });
        kvSet(recoveryKey(id), {
          payload: { title, items, manual_outcome: manualOutcome,
                     manual_outcome_reason: manualOutcomeReason },
          savedAt: Date.now(),
        });
      } catch { /* best-effort */ }
    };
    window.addEventListener("guardian:checkpoint", onCheckpoint);
    return () => window.removeEventListener("guardian:checkpoint", onCheckpoint);
  }, [id, title, items, manualOutcome, manualOutcomeReason, photosPartial]);

  // Update-safety: when a PWA update is about to apply, durably save the draft
  // (answers, notes, annotations, inline photos) to IndexedDB and attempt the
  // server/offline-queue save. We register a completion promise so the updater
  // AWAITS us and only activates once the work is durably persisted. The promise
  // REJECTS only if the durable local write fails — which pauses the update.
  useEffect(() => {
    const onFlush = (e) => {
      if (locked) return; // nothing unsaved to protect on a locked inspection
      const done = (async () => {
        const payload = {
          title, items, manual_outcome: manualOutcome,
          manual_outcome_reason: manualOutcomeReason,
          ...(photosPartial ? { photos_partial: true } : {}),
        };
        // Durable local snapshot first — this is what makes work survive the
        // reload even if the server is unreachable. If THIS throws we cannot
        // safely update.
        await kvSet(draftKey(id), { payload, savedAt: Date.now() });
        await kvSet(recoveryKey(id), { payload, savedAt: Date.now() });
        // Best-effort push to the server / offline queue (never throws fatally).
        try { await save(false); } catch { /* queued offline — already durable */ }
      })();
      if (e.detail && typeof e.detail.waitUntil === "function") e.detail.waitUntil(done);
    };
    window.addEventListener("psc:flush-before-update", onFlush);
    return () => window.removeEventListener("psc:flush-before-update", onFlush);
  }, [id, title, items, manualOutcome, manualOutcomeReason, photosPartial, locked, save]);

  // Re-render the "Saved 3s ago" text periodically
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // ---- Periodic cloud autosave safety net ----
  // Quietly pushes pending changes to the server every 60s while anything is
  // unsynced (dirty / offline-queued / failed), and immediately when the
  // connection returns. Never blocks the checklist — it's the same silent save.
  const saveRef = useMemo(() => ({ current: null }), []);
  saveRef.current = save;
  const syncStateRef = useMemo(() => ({ current: "idle" }), []);
  syncStateRef.current = autoSaveState;
  useEffect(() => {
    if (loading || locked) return;
    const pendingStates = ["dirty", "offline", "retrying"];
    const kick = () => {
      if (!navigator.onLine) return;
      if (pendingStates.includes(syncStateRef.current)) saveRef.current?.(false);
    };
    const t = setInterval(kick, 60000);
    const onOnline = () => setTimeout(kick, 1500);
    const onOffline = () => {
      if (pendingStates.includes(syncStateRef.current)) setAutoSaveState("offline");
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      clearInterval(t);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [loading, locked, saveRef, syncStateRef]);

  // Deep-link from the Fire Safety Work Tracker: ?q=<question_id> scrolls to
  // and briefly highlights the original checklist question.
  useEffect(() => {
    if (loading) return;
    const qid = new URLSearchParams(window.location.search).get("q");
    if (!qid) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`q-${qid}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-4", "ring-amber-400", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-4", "ring-amber-400", "ring-offset-2"), 3500);
    }, 700);
    return () => clearTimeout(t);
  }, [loading]);

  // Warn the user if they try to close the tab while there are unsaved changes.
  useEffect(() => {
    const beforeUnload = (e) => {
      if (autoSaveState === "dirty" || autoSaveState === "saving" || autoSaveState === "retrying") {
        e.preventDefault();
        e.returnValue = "You have unsaved inspection changes.";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [autoSaveState]);

  const onLock = async (signatureData) => {
    try {
      const r = await api.post(`/inspections/${id}/lock`, signatureData ? { signature: signatureData } : {});
      setInsp(r.data);
      toast.success("Inspection signed & locked");
      setSignOpen(false);
    } catch { toast.error("Could not lock"); }
  };

  const onUnlock = async () => {
    const reason = window.prompt("Why are you unlocking this inspection? (recorded in audit log)");
    if (!reason || reason.trim().length < 3) return;
    setUnlocking(true);
    try {
      const r = await api.post(`/inspections/${id}/unlock`, { reason: reason.trim() });
      setInsp(r.data);
      toast.success("Inspection unlocked");
    } catch { toast.error("Could not unlock"); }
    finally { setUnlocking(false); }
  };

  const generateReport = async () => {
    await save(false);
    setGenLoading(true);
    try {
      const r = await api.post(`/inspections/${id}/generate-report`);
      setInsp(r.data);
      setReportOpen(true);
      toast.success("AI report generated");
    } catch (e) {
      if (e?.response?.status === 402) {
        setUpgrade({
          open: true,
          feature: "AI compliance reports",
          message: e?.response?.data?.detail || "",
        });
      } else {
        toast.error(e?.response?.data?.detail || "Report generation failed");
      }
    } finally { setGenLoading(false); }
  };

  const labelFor = (qid) => {
    for (const sec of schema || []) for (const it of sec.items || []) if (it.id === qid) return it.label;
    for (const sec of insp?.template_snapshot?.sections || []) for (const q of sec.questions || []) if (q.id === qid) return q.label;
    return null;
  };

  const downloadPdf = async ({ skipSave = false } = {}) => {
    setPdfLoading(true);
    try {
      let blob = null;
      if (!offlineMode) {
        // online path: save latest then fetch fresh PDF, unless caller has
        // already persisted the items directly (markAllEvidenceAsNo does this).
        if (!skipSave) {
          try { await save(false); } catch { /* save may queue offline */ }
        }
        try {
          const token = localStorage.getItem("psc_token");
          const res = await fetch(`${API}/inspections/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) blob = await res.blob();
        } catch { /* will fall through to cache */ }
      }
      if (!blob) {
        // offline or fetch failed → try the Cache-Storage copy stashed at finalise time
        blob = await getCachedPdf(id);
        if (blob) toast.info("Offline — opened the cached PDF");
      }
      if (!blob) { toast.error("PDF unavailable offline. Finalise the report while online to cache it."); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `inspection-${id.slice(0,8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
    finally { setPdfLoading(false); }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this inspection permanently?")) return;
    try { await api.delete(`/inspections/${id}`); toast.success("Deleted"); navigate("/"); }
    catch { toast.error("Delete failed"); }
  };

  // Live compliance summary (matches backend logic)
  const stats = useMemo(() => {
    if (usingCustomTpl) {
      const s = customStats || { total: 0, answered: 0, unanswered: 0, flagged: 0 };
      const score = s.answered - s.flagged;
      let status = "draft";
      if (s.flagged > 0) status = "non_compliant";
      else if (s.unanswered === 0 && s.total > 0) status = "compliant";
      return { score, total: s.total, flagged: s.flagged, unanswered: s.unanswered, status };
    }
    if (!schema) return { score: 0, total: 0, flagged: 0, unanswered: 0, status: "draft" };
    let score = 0, total = 0, flagged = 0, unanswered = 0;
    for (const sec of schema) for (const it of sec.items) {
      if (!it.compliance) continue;
      if (!isVisible(it, items)) continue;
      total += 1;
      const a = (items[it.id] || {}).answer || "";
      if (a === "yes" || a === "na") score += 1;
      else if (a === "no") flagged += 1;
      else unanswered += 1;
    }
    let status = "draft";
    if (flagged > 0) status = "non_compliant";
    else if (unanswered === 0 && total > 0) status = "compliant";
    return { score, total, flagged, unanswered, status };
  }, [schema, items, usingCustomTpl, customStats]);

  // Evidence-only items: user attached photos or wrote substantial notes but
  // never picked a YES/NO answer. These are the #1 cause of "my non-compliance
  // didn't appear on the report" — the certifier documented findings without
  // ticking the compliance toggle.
  const evidenceOnly = useMemo(() => {
    if (usingCustomTpl || !schema) return [];
    const rows = [];
    for (const sec of schema) for (const it of sec.items) {
      if (!it.compliance) continue;
      if (!isVisible(it, items)) continue;
      const v = items[it.id] || {};
      const answered = v.answer === "yes" || v.answer === "no" || v.answer === "na";
      const hasEvidence = (v.photos && v.photos.length > 0) || (v.notes && v.notes.trim().length >= 15);
      if (!answered && hasEvidence) {
        rows.push({ id: it.id, label: it.label, section: sec.title, photoCount: (v.photos||[]).length, notesLen: (v.notes||"").length });
      }
    }
    return rows;
  }, [schema, items, usingCustomTpl]);

  const [preGenerateOpen, setPreGenerateOpen] = useState(false);

  const runDownloadPdf = downloadPdf;
  const downloadPdfWithCheck = () => {
    if (evidenceOnly.length > 0) {
      setPreGenerateOpen(true);
    } else {
      runDownloadPdf();
    }
  };

  const markAllEvidenceAsNo = async () => {
    // Compute the next items map LOCALLY — avoids the React setState / useCallback
    // stale-closure race where the deferred save() would PUT pre-update items.
    const nextItems = { ...items };
    for (const row of evidenceOnly) {
      const cur = nextItems[row.id] || { answer: "", notes: "", photos: [] };
      nextItems[row.id] = { ...cur, answer: "no" };
    }
    setItems(nextItems);
    setPreGenerateOpen(false);
    // Direct PUT bypassing the useCallback save (which captures old items).
    try {
      const r = await api.put(`/inspections/${id}`, { title, items: nextItems });
      setInsp(r.data);
      clearInspectionFromQueue(id);
    } catch (e) {
      if (isNetworkError(e)) {
        queueInspectionSave(id, { title, items: nextItems });
        toast.info("Saved offline — PDF may reflect last-synced state until you reconnect");
      } else {
        toast.error("Save failed — please try again");
        return;
      }
    }
    await runDownloadPdf({ skipSave: true });
  };

  const continueAnyway = async () => {
    setPreGenerateOpen(false);
    await runDownloadPdf();
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin"/></div>;
  if (!usingCustomTpl && !schema) return (
    <div className="py-24 px-6 text-center text-slate-600" data-testid="schema-unavailable-offline">
      <p className="font-black text-slate-900">Checklist unavailable offline</p>
      <p className="text-sm mt-1">Open the app once while online so the checklist can be stored on this device for offline use.</p>
    </div>
  );
  if (!insp) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <Link to="/" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-slate-900">
          <ArrowLeft size={16} strokeWidth={2.5}/> Back to inspections
        </Link>
        <div className="flex items-center gap-2.5 flex-wrap">
          <StatusChip status={stats.status} testid="header-status-chip" />
          <AutoSaveIndicator state={autoSaveState} lastSavedAt={lastSavedAt}/>
          <button onClick={() => save(true)} disabled={saving} data-testid="save-button"
            className="h-11 px-4 inline-flex items-center gap-2 bg-white border-2 border-slate-900 text-slate-900 font-bold text-sm hover:bg-slate-100 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16} strokeWidth={2.5}/>}Save
          </button>
          <VoiceInspection
            inspectionId={id}
            onApplyAnswers={(answers) => {
              // Apply parsed answers to the checklist items array
              answers.forEach((a) => {
                updateItem(a.question_id, { answer: String(a.proposed_answer ?? "") });
              });
            }}
          />
          <button onClick={generateReport} disabled={genLoading} data-testid="generate-report-button"
            title={insp.ai_report ? "Regenerate the AI compliance report" : "Generate an AI compliance report"}
            className="h-11 px-4 inline-flex items-center gap-2 bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] font-bold text-sm hover:bg-[#0F0E2E] disabled:opacity-60">
            {genLoading ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16} strokeWidth={2.5}/>}
            AI report
            {!isPro && <LockIcon size={12} strokeWidth={3} className="text-amber-300" data-testid="ai-report-pro-lock" />}
          </button>
          <button onClick={downloadPdfWithCheck} disabled={pdfLoading} data-testid="download-pdf-button"
            className="h-11 px-4 inline-flex items-center gap-2 bg-white border-2 border-slate-300 text-slate-900 font-bold text-sm hover:border-slate-900 disabled:opacity-60">
            {pdfLoading ? <Loader2 size={16} className="animate-spin"/> : <Download size={16} strokeWidth={2.5}/>}PDF
          </button>
          <div className="relative">
            <button onClick={() => setMoreOpen((o) => !o)} data-testid="more-actions-button"
              className={`h-11 px-4 inline-flex items-center gap-2 border-2 font-bold text-sm ${moreOpen ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 text-slate-900 hover:border-slate-900"}`}>
              <MoreHorizontal size={16} strokeWidth={2.5}/>More
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} data-testid="more-menu-backdrop" />
                <div className="absolute right-0 top-full mt-1 w-56 bg-white border-2 border-slate-200 shadow-2xl z-50 py-1" data-testid="more-menu-panel">
                  {insp?.template_snapshot?.photo_report_config?.enabled !== false && (
                    <button onClick={() => { setMoreOpen(false); navigate(`/inspections/${id}/photo-report`); }} data-testid="create-photo-report-button"
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-[#00F2C3]/15 hover:text-[#0B8F75] active:bg-[#00F2C3]/25 text-left transition-colors">
                      <Camera size={15} strokeWidth={2.5}/>Photo report
                    </button>
                  )}
                  <button onClick={async () => { setMoreOpen(false); downloadInspectionPhotosZip({ ...insp, items: await resolveAllLazyPhotos(id, items) }, labelFor); }} data-testid="save-photos-button"
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-[#00F2C3]/15 hover:text-[#0B8F75] active:bg-[#00F2C3]/25 text-left transition-colors">
                    <ImageDown size={15} strokeWidth={2.5}/>Save photos
                  </button>
                  <button onClick={() => { setMoreOpen(false); setEmailOpen(true); }} data-testid="email-owner-button"
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-[#00F2C3]/15 hover:text-[#0B8F75] active:bg-[#00F2C3]/25 text-left transition-colors">
                    <Mail size={15} strokeWidth={2.5}/>Email owner
                  </button>
                  <button onClick={() => { setMoreOpen(false); setShareOpen(true); }} data-testid="share-with-customer-button"
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-[#00F2C3]/15 hover:text-[#0B8F75] active:bg-[#00F2C3]/25 text-left transition-colors">
                    <Share2 size={15} strokeWidth={2.5}/>Share with owner
                    {!isPro && <LockIcon size={12} strokeWidth={3} className="text-amber-600 ml-auto" data-testid="share-pro-lock" />}
                  </button>
                  <button onClick={() => { setMoreOpen(false); navigate(`/inspections/${id}/audit`); }} data-testid="open-audit-log-button"
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-[#00F2C3]/15 hover:text-[#0B8F75] active:bg-[#00F2C3]/25 text-left transition-colors">
                    <History size={15} strokeWidth={2.5}/>Audit log
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  {insp?.compliance_certificate_issued_at ? (
                    <button onClick={() => { setMoreOpen(false); setCorrectStatusOpen(true); }} data-testid="correct-status-button"
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-amber-700 hover:bg-amber-50 text-left transition-colors">
                      <RotateCcw size={15} strokeWidth={2.5}/>Correct status
                    </button>
                  ) : (
                    <div data-testid="correct-status-disabled"
                      title="Available after &ldquo;Compliance Certificate Issued&rdquo; has been recorded"
                      className="w-full px-4 py-2 text-left cursor-not-allowed select-none">
                      <div className="flex items-center gap-2.5 text-sm font-bold text-slate-300">
                        <RotateCcw size={15} strokeWidth={2.5}/>Correct status
                      </div>
                      <div className="pl-[26px] text-[11px] font-semibold text-slate-400 leading-tight mt-0.5">
                        Available after &ldquo;Compliance Certificate Issued&rdquo; has been recorded.
                      </div>
                    </div>
                  )}
                  <button onClick={() => { setMoreOpen(false); onDelete(); }} data-testid="delete-inspection-button"
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-red-700 hover:bg-red-50 text-left">
                    <Trash2 size={15} strokeWidth={2.5}/>Delete inspection
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="border-b-2 border-slate-200 pb-6 mb-8">
        {offlineDraft && (
          <div data-testid="offline-draft-banner" className="mb-4 px-4 py-3 bg-amber-50 border-2 border-amber-300 text-sm text-amber-900">
            <strong>You&apos;re offline.</strong> Everything you enter is saved safely on this device and will sync to the server automatically the moment you&apos;re back online. No data will be lost.
          </div>
        )}
        {offlineMode && (
          <div data-testid="offline-cached-banner" className="mb-4 px-4 py-3 bg-amber-50 border-2 border-amber-300 text-sm text-amber-900">
            <strong>Offline mode.</strong> You&apos;re viewing the cached finalised report
            {insp.ai_report_finalized_at ? ` from ${new Date(insp.ai_report_finalized_at).toLocaleString()}` : ""}.
            Editing is disabled until you reconnect.
          </div>
        )}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500" data-testid="inspection-kicker">
          {usingCustomTpl
            ? `${insp.template_snapshot?.name || "Custom checklist"}${insp.template_version ? ` · v${insp.template_version}` : ""}`
            : "AS1926.1-2012 · SPA 1992"}
        </p>
        <input
          data-testid="inspection-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Inspection title (e.g. Vanessa @ Rueben, 29 Jun)"
          className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-1 w-full bg-transparent focus:outline-none focus:bg-slate-50 px-1"
        />
        <div className="mt-3 flex gap-6 text-sm text-slate-600 flex-wrap">
          <span><strong className="text-slate-900">Score:</strong> {stats.score}/{stats.total}</span>
          <span><strong className="text-slate-900">Flagged:</strong> {stats.flagged}</span>
          <span><strong className="text-slate-900">Unanswered:</strong> {stats.unanswered}</span>
        </div>
        <div className="mt-4 p-3 border-2 border-purple-200 bg-purple-50 flex items-start gap-2.5" data-testid="ai-features-hint">
          <span className="text-purple-700 font-black text-xs uppercase tracking-[0.15em] shrink-0 mt-0.5">AI Tools</span>
          <p className="text-xs text-purple-900 leading-relaxed">
            Tap <strong>Voice inspection</strong> to fill the checklist by talking · <strong>Use my location</strong> on the address field auto-fills via GPS · <strong>Dictate</strong> button on any note lets you speak instead of type · Upload a photo then click <strong>AI check</strong> for Claude 4.5 fence-height &amp; gap analysis.
          </p>
        </div>
        {evidenceOnly.length > 0 && (
          <div className="mt-3 p-3 border-2 border-red-400 bg-red-50 flex items-start gap-2.5" data-testid="evidence-only-warning">
            <AlertTriangle size={16} className="text-red-700 shrink-0 mt-0.5" strokeWidth={2.5}/>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-black uppercase tracking-[0.15em] text-red-800">Non-compliance won&apos;t show on the report</p>
              <p className="text-xs text-red-900 mt-1 leading-relaxed">
                You have <strong>{evidenceOnly.length}</strong> item(s) with photos or detailed notes but no YES / NO answer.
                Tap the <strong>NO</strong> button on each of these items so they appear as non-compliances on your report:
                <span className="font-bold"> {evidenceOnly.slice(0, 4).map((r) => r.label).join(", ")}{evidenceOnly.length > 4 ? `, +${evidenceOnly.length - 4} more` : ""}</span>.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className={locked ? "pointer-events-none opacity-70 select-none" : ""} data-testid="form-wrapper" aria-disabled={locked}>
            {usingCustomTpl ? (
              <CustomChecklistRenderer
                snapshot={insp.template_snapshot}
                items={items}
                updateItem={(qid, patch) => updateItem(qid, patch)}
                onStats={setCustomStats}
                locked={locked}
              />
            ) : (
              schema.map((sec) => (
                <SectionCard key={sec.id} section={sec} items={items} updateItem={updateItem} inspectionId={id} />
              ))
            )}
          </div>

          {/* Final Inspection Outcome — certifier's professional override */}
          <FinalOutcomeSection
            value={manualOutcome}
            reason={manualOutcomeReason}
            calculated={stats.status}
            disabled={locked}
            onChange={(next, nextReason) => {
              setManualOutcome(next);
              if (nextReason !== undefined) setManualOutcomeReason(nextReason);
            }}
          />
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 text-white p-6 border-2 border-slate-900 sticky top-20">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70">Live Summary</p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div><div className="text-[10px] uppercase opacity-70 font-bold">Score</div><div className="text-2xl font-black">{stats.score}/{stats.total}</div></div>
              <div><div className="text-[10px] uppercase opacity-70 font-bold">Flagged</div><div className="text-2xl font-black text-red-300">{stats.flagged}</div></div>
              <div><div className="text-[10px] uppercase opacity-70 font-bold">Open</div><div className="text-2xl font-black text-amber-300">{stats.unanswered}</div></div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="text-[10px] uppercase opacity-70 font-bold mb-2">Status</div>
              <StatusChip status={stats.status} testid="summary-status" />
            </div>
            <FlaggedList schema={schema} items={items} usingCustomTpl={usingCustomTpl} snapshot={insp.template_snapshot} />
          </div>

          <div className="bg-white border-2 border-slate-200 p-6">
            <button onClick={() => setReportOpen(o => !o)} className="w-full flex items-center justify-between text-left" data-testid="toggle-report">
              <div className="flex items-center gap-2">
                <FileText size={18} strokeWidth={2.5}/>
                <span className="font-black text-slate-900">AI Compliance Report</span>
              </div>
              <ChevronDown size={18} className={`transition-transform ${reportOpen ? "rotate-180" : ""}`} strokeWidth={2.5}/>
            </button>
            {reportOpen && (
              <div className="mt-4" data-testid="ai-report-block">
                {genLoading ? (
                  <div data-testid="ai-report-loading" className="flex items-center gap-2 text-sm text-slate-600">
                    <Loader2 className="animate-spin" size={16}/> Generating with Claude Sonnet 4.5…
                  </div>
                ) : insp.ai_report ? (
                  <ReportEditor
                    inspectionId={id}
                    reportText={insp.ai_report}
                    finalized={!!insp.ai_report_finalized}
                    finalizedAt={insp.ai_report_finalized_at}
                    hasOriginal={!!insp.ai_report_original}
                    onUpdated={(updated) => setInsp(updated)}
                    renderReadOnly={(t) => (
                      <article className="prose prose-sm max-w-none">
                        <MarkdownLite text={t} />
                      </article>
                    )}
                  />
                ) : (
                  <p data-testid="ai-report-empty" className="text-sm text-slate-600">
                    Fill in the checklist, then click <strong>Generate AI report</strong> for a plain-English compliance report with fixes referenced to AS1926.1-2012.
                  </p>
                )}
              </div>
            )}
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            NOTE: This is not a &ldquo;Compliance Certificate&rdquo; issued under Section 22D of the Swimming Pools Act 1992 or Part 4A of the Environmental Planning and Assessment Act 1979.
          </p>

          <DocumentsBillingCard inspectionId={id} />

          <EmailHistory inspectionId={id} />
        </div>
      </div>

      <EmailReportDialog inspectionId={id} open={emailOpen} onClose={() => setEmailOpen(false)} />
      {correctStatusOpen && (
        <CorrectStatusModal
          inspectionId={id}
          address={insp?.property_address || insp?.title || ""}
          onClose={() => setCorrectStatusOpen(false)}
          onDone={async (res) => {
            setCorrectStatusOpen(false);
            try { const r = await api.get(`/inspections/${id}`); setInsp(r.data); } catch { /* best effort */ }
            const s = res?.restored;
            if (s) {
              toast.success(s.overdue
                ? `Status corrected — this statutory action is OVERDUE by ${Math.abs(s.days_remaining)} days.`
                : `Status corrected — returned to statutory workflow. Council submission due ${String(s.due_at).slice(0, 10)} (${s.days_remaining} days remaining).`,
                { duration: 9000 });
            } else {
              toast.success("Status corrected — returned to statutory non-compliance workflow.");
            }
          }}
        />
      )}
      <NoticeDialog
        inspectionId={id}
        defaultInspectionDate={(items.inspection_datetime || {}).answer || ""}
        open={noticeOpen}
        onClose={() => setNoticeOpen(false)}
      />
      <SignaturePad open={signOpen} onClose={() => setSignOpen(false)} onSave={onLock} />
      <ShareDialog
        inspectionId={id}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        existingToken={insp.portal_token}
        onUpgradeNeeded={(msg) => setUpgrade({ open: true, feature: "the customer portal", message: msg })}
      />
      <UpgradePrompt
        open={upgrade.open}
        feature={upgrade.feature}
        message={upgrade.message}
        onClose={() => setUpgrade((u) => ({ ...u, open: false }))}
      />

      {locked && (
        <div className="mt-8 bg-emerald-50 border-2 border-emerald-300 p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="locked-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-600 text-white flex items-center justify-center"><CheckCircle2 size={20} strokeWidth={2.5}/></div>
            <div>
              <p className="font-black text-emerald-900">Signed &amp; Locked</p>
              <p className="text-sm text-emerald-800">Locked at {insp.locked_at ? new Date(insp.locked_at).toLocaleString() : ""}. This inspection is the legal source of truth. To make corrections, unlock with a reason (logged for audit).</p>
            </div>
          </div>
          <button onClick={onUnlock} disabled={unlocking} data-testid="unlock-button"
            className="h-11 px-4 inline-flex items-center gap-2 bg-white border-2 border-emerald-700 text-emerald-900 font-bold text-sm hover:bg-emerald-100 disabled:opacity-60 whitespace-nowrap">
            {unlocking ? <Loader2 size={16} className="animate-spin"/> : <Unlock size={16} strokeWidth={2.5}/>}Unlock
          </button>
        </div>
      )}

      {!locked && stats.total > 0 && stats.unanswered === 0 && (
        <div className="mt-8 bg-gradient-to-br from-[#1E1B4B] to-black border-2 border-[#1E1B4B] p-6 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="complete-cta">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-teal-300">Step 5 — Finalise</p>
            <h2 className="text-xl sm:text-2xl font-black mt-1">Sign &amp; lock this inspection</h2>
            <p className="text-sm text-slate-300 mt-1">Capture your signature and lock the record so it becomes the legally-binding version.</p>
          </div>
          <button onClick={() => setSignOpen(true)} data-testid="sign-and-lock-button"
            className="h-12 px-5 inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold border-2 border-teal-500 whitespace-nowrap">
            <Lock size={18} strokeWidth={2.5}/>Sign &amp; lock
          </button>
        </div>
      )}

      {stats.flagged > 0 && (
        <div className="mt-12 bg-gradient-to-br from-[#1E1B4B] via-[#1E1B4B] to-black border-2 border-[#1E1B4B] p-8 text-white" data-testid="notice-cta">
          <div className="flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-teal-300">Step 4 — Formal Notice</p>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-1">Generate Notice of Non-Compliance</h2>
              <p className="text-sm text-slate-300 mt-2 max-w-xl">
                Build a certifier-grade, multi-page PDF Notice from the {stats.flagged} flagged item{stats.flagged>1?"s":""}, with cover page, AS 1926.1-2012 reference, numbered findings &amp; photo evidence pages.
              </p>
            </div>
            <button onClick={() => setNoticeOpen(true)} data-testid="open-notice-button"
              className="h-14 px-6 inline-flex items-center justify-center gap-2 bg-teal-500 text-white border-2 border-teal-500 hover:bg-teal-400 font-bold text-base whitespace-nowrap">
              <FileWarning size={20} strokeWidth={2.5}/>
              Generate Notice
            </button>
          </div>
        </div>
      )}
      {preGenerateOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setPreGenerateOpen(false)} data-testid="pre-generate-modal">
          <div className="bg-white border-2 border-slate-200 max-w-xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <header className="px-5 py-4 border-b-2 border-amber-300 bg-amber-50">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-800">Warning · read before generating</p>
              <h2 className="text-lg font-black text-slate-900 mt-1">You have {evidenceOnly.length} item(s) with photos or notes but no YES/NO answer</h2>
              <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                These items look like non-compliances you documented, but you didn&apos;t tap the <strong>NO</strong> toggle on each one. As a result the report will show <strong>&quot;No non-compliances identified&quot;</strong>.
              </p>
            </header>
            <div className="p-5">
              <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                {evidenceOnly.map((row) => (
                  <li key={row.id} className="p-2.5 border-2 border-slate-200 bg-slate-50 text-sm" data-testid={`evidence-only-${row.id}`}>
                    <div className="font-bold text-slate-900">{row.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Section: {row.section} · {row.photoCount} photo(s) · {row.notesLen} chars of notes
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <footer className="px-5 py-4 border-t-2 border-slate-200 bg-slate-50 flex flex-wrap justify-end gap-2">
              <button onClick={() => setPreGenerateOpen(false)} data-testid="pre-generate-cancel" className="h-11 px-4 border-2 border-slate-300 bg-white text-slate-900 font-bold text-xs uppercase tracking-wider">Cancel &amp; go back</button>
              <button onClick={continueAnyway} data-testid="pre-generate-continue" className="h-11 px-4 border-2 border-slate-500 bg-white text-slate-900 font-bold text-xs uppercase tracking-wider hover:bg-slate-100">Continue anyway</button>
              <button onClick={markAllEvidenceAsNo} data-testid="pre-generate-mark-no" className="h-11 px-5 border-2 border-red-600 bg-red-600 text-white font-bold text-xs uppercase tracking-wider hover:bg-red-700 inline-flex items-center gap-1.5">
                <AlertTriangle size={12} strokeWidth={2.5}/> Mark all NO &amp; generate
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

const FlaggedList = ({ schema, items, usingCustomTpl, snapshot }) => {
  const [showAll, setShowAll] = useState(false);
  const flagged = [];
  if (usingCustomTpl && snapshot) {
    // Use the same eval logic as the renderer
    for (const sec of snapshot.sections || []) {
      for (const q of sec.questions || []) {
        if (!q.flag_when) continue;
        const ans = (items[q.id] || {}).answer;
        if (ans === "" || ans == null) continue;
        const a = String(ans).toLowerCase();
        const t = String(q.flag_when.value ?? "").toLowerCase();
        const op = q.flag_when.op;
        let hit = false;
        if (op === "equals") hit = a === t;
        else if (op === "not_equals") hit = a !== t;
        else if (op === "in") hit = t.split(",").map(s=>s.trim()).includes(a);
        else if (op === "gt") hit = Number(a) > Number(t);
        else if (op === "gte") hit = Number(a) >= Number(t);
        else if (op === "lt") hit = Number(a) < Number(t);
        else if (op === "lte") hit = Number(a) <= Number(t);
        if (hit) flagged.push({ id: q.id, label: q.label });
      }
    }
  } else if (schema) {
    for (const sec of schema) for (const it of sec.items) {
      if (!it.compliance || !isVisible(it, items)) continue;
      if ((items[it.id] || {}).answer === "no") flagged.push(it);
    }
  }
  if (!flagged.length) return null;
  const visible = showAll ? flagged : flagged.slice(0, 6);
  return (
    <div className="mt-4 pt-4 border-t border-white/20">
      <div className="text-[10px] uppercase opacity-70 font-bold mb-2 flex items-center gap-1"><Flag size={11} strokeWidth={3}/> Flagged items ({flagged.length})</div>
      <ul className="space-y-1.5 text-xs text-red-100 list-disc pl-5" data-testid="flagged-items-list">
        {visible.map((it) => (
          <li key={it.id} className="leading-snug">{(it.label || "").replace(/^\(\d+[a-z]?\)\s*/, "")}</li>
        ))}
      </ul>
      {flagged.length > 6 && (
        <button
          type="button"
          data-testid="flagged-items-toggle"
          onClick={() => setShowAll((s) => !s)}
          className="mt-2 text-[11px] font-bold uppercase tracking-wider text-white underline underline-offset-2 hover:opacity-80"
        >
          {showAll ? "Show less" : `Show all ${flagged.length} flagged items`}
        </button>
      )}
    </div>
  );
};

const AutoSaveIndicator = ({ state, lastSavedAt }) => {
  const wrap = "inline-flex items-center gap-1.5 text-xs px-2";
  if (state === "saving") return (
    <span data-testid="autosave-state" title="Syncing to cloud" className={`${wrap} text-sky-700`}>
      <CloudUpload size={13} className="animate-pulse"/><span className="hidden sm:inline">Syncing to cloud…</span>
    </span>
  );
  if (state === "offline") return (
    <span data-testid="autosave-state" title="Offline — waiting to sync" className={`${wrap} text-amber-700 font-bold`}>
      <CloudOff size={13}/><span className="hidden sm:inline">Offline — waiting to sync</span>
    </span>
  );
  if (state === "retrying") return (
    <span data-testid="autosave-state" title="Sync failed — retrying" className={`${wrap} text-amber-700 font-bold`}>
      <RefreshCw size={13} className="animate-spin" style={{ animationDuration: "2.5s" }}/><span className="hidden sm:inline">Sync failed — retrying</span>
    </span>
  );
  if (state === "error") return (
    <span data-testid="autosave-state" className={`${wrap} text-red-600 font-bold`}>
      Save failed
    </span>
  );
  if (state === "dirty") return (
    <span data-testid="autosave-state" title="Saved on device" className={`${wrap} text-slate-500`}>
      <Save size={13} strokeWidth={2.5}/><span className="hidden sm:inline">Saved on device</span>
    </span>
  );
  if (state === "saved" && lastSavedAt) {
    const s = Math.floor((Date.now() - lastSavedAt) / 1000);
    const label = s < 5 ? "just now" : s < 60 ? `${s}s ago` : `${Math.floor(s / 60)}m ago`;
    return (
      <span data-testid="autosave-state" title={`Saved to cloud ${label}`} className={`${wrap} text-emerald-700`}>
        <Cloud size={13} strokeWidth={2.5}/><span className="hidden sm:inline">Saved to cloud {label}</span>
      </span>
    );
  }
  return null;
};

const FinalOutcomeSection = ({ value, reason, calculated, disabled, onChange }) => {
  const pick = (next) => onChange(next === value ? null : next, reason);
  const currentLabel = value === "compliant" ? "COMPLIANT" : value === "non_compliant" ? "NON-COMPLIANT" : null;
  return (
    <section className="bg-white border-2 border-[#1E1B4B] p-5 sm:p-6" data-testid="final-outcome-section">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-8 w-8 shrink-0 bg-[#1E1B4B] text-white inline-flex items-center justify-center">
          <CheckCircle2 size={18} strokeWidth={2.5}/>
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-black uppercase tracking-wide text-slate-900 leading-tight">Final Inspection Outcome</h2>
          <p className="text-xs text-slate-500 mt-1">
            Your professional determination. Overrides the auto-calculated result on the dashboard, PDF and AI report. Leave blank to use the calculated outcome
            {calculated ? <span className="font-bold text-slate-700"> (currently {String(calculated).replace(/-/g, " ")})</span> : null}.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <button
          type="button"
          onClick={() => pick("compliant")}
          disabled={disabled}
          data-testid="final-outcome-compliant"
          className={`p-4 border-2 text-left transition-all ${value === "compliant" ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-emerald-400"} disabled:opacity-60`}>
          <div className={`text-xs font-bold uppercase tracking-[0.15em] ${value === "compliant" ? "text-emerald-800" : "text-slate-500"}`}>Option 1</div>
          <div className="mt-1 flex items-center gap-2">
            <div className={`h-5 w-5 border-2 ${value === "compliant" ? "border-emerald-600 bg-emerald-600" : "border-slate-300"}`}>
              {value === "compliant" && <CheckCircle2 size={14} className="text-white"/>}
            </div>
            <span className="text-lg font-black text-slate-900">Compliant</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">The pool meets all statutory requirements as determined by the certifier.</p>
        </button>
        <button
          type="button"
          onClick={() => pick("non_compliant")}
          disabled={disabled}
          data-testid="final-outcome-noncompliant"
          className={`p-4 border-2 text-left transition-all ${value === "non_compliant" ? "border-red-600 bg-red-50" : "border-slate-200 hover:border-red-400"} disabled:opacity-60`}>
          <div className={`text-xs font-bold uppercase tracking-[0.15em] ${value === "non_compliant" ? "text-red-800" : "text-slate-500"}`}>Option 2</div>
          <div className="mt-1 flex items-center gap-2">
            <div className={`h-5 w-5 border-2 ${value === "non_compliant" ? "border-red-600 bg-red-600" : "border-slate-300"}`}>
              {value === "non_compliant" && <CheckCircle2 size={14} className="text-white"/>}
            </div>
            <span className="text-lg font-black text-slate-900">Non-Compliant</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">The pool does not meet one or more statutory requirements.</p>
        </button>
      </div>

      {value && (
        <div className="mt-4">
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">
              Reason for override (recorded in audit log)
            </span>
            <textarea
              data-testid="final-outcome-reason"
              rows={2}
              value={reason || ""}
              onChange={(e) => onChange(value, e.target.value)}
              disabled={disabled}
              placeholder="e.g. Notwithstanding minor cosmetic non-compliances, the pool meets the statutory intent…"
              className="w-full p-2 text-sm border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none disabled:opacity-60"/>
          </label>
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 mt-3">
            <AlertTriangle size={11} strokeWidth={2.5} className="inline mr-1 -mt-0.5"/>
            You have manually set this inspection to <b>{currentLabel}</b>. This will appear on the dashboard, PDF and AI report.
          </p>
        </div>
      )}
    </section>
  );
};

const SectionCard = ({ section, items, updateItem, inspectionId }) => {
  const visibleItems = section.items.filter((it) => isVisible(it, items));
  if (!visibleItems.length) return null;
  return (
    <section className="bg-white border-2 border-slate-200 p-5 sm:p-6">
      <h2 className="text-lg font-black uppercase tracking-wide text-slate-900 mb-4">{section.title}</h2>
      <div className="space-y-6">
        {visibleItems.map((it) => (
          <ItemRow key={it.id} item={it} value={items[it.id] || { answer: "", notes: "", photos: [] }} updateItem={updateItem} inspectionId={inspectionId} />
        ))}
      </div>
    </section>
  );
};

// Memoised: only the row being edited re-renders on keystrokes — sibling rows
// (with their base64 photo strips) are skipped, keeping typing smooth.
const ItemRow = memo(({ item, value, updateItem, inspectionId }) => {
  const set = (patch) => updateItem(item.id, patch);
  const [lightbox, setLightbox] = useState(null);
  // Phone back button while a photo is enlarged: close the photo, stay on page.
  useOverlayBackClose(!!lightbox, () => setLightbox(null));
  const tone = value.answer === "yes" ? "border-l-emerald-500" : value.answer === "no" ? "border-l-red-500" : value.answer === "na" ? "border-l-slate-400" : "border-l-slate-200";
  const refs = item.ref_images || [];

  return (
    <div className={`pl-4 border-l-4 ${tone} transition-colors`}>
      <p className="text-sm font-semibold text-slate-900 leading-snug whitespace-pre-line">
        {item.label}
        {item.ref && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">({item.ref})</span>}
      </p>
      {item.help && <p className="text-xs text-slate-500 mt-1">{item.help}</p>}

      {refs.length > 0 && (
        <div className="mt-2" data-testid={`refs-panel-${item.id}`}>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {refs.map((url, idx) => (
              <button
                key={url}
                type="button"
                onClick={() => setLightbox(url)}
                data-testid={`refs-thumb-${item.id}-${idx}`}
                className="shrink-0 border-2 border-teal-200 hover:border-teal-600 bg-white overflow-hidden"
                title="Tap to enlarge"
              >
                <img
                  src={url}
                  alt={`Reference example for ${item.id}`}
                  loading="lazy"
                  className={refs.length === 1
                    ? "h-40 sm:h-48 w-auto max-w-full object-contain"
                    : "h-32 sm:h-40 w-auto object-contain"}
                />
              </button>
            ))}
          </div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-teal-800 mt-1">
            Reference — non-compliance example{refs.length > 1 ? "s" : ""} · tap to enlarge
          </p>
        </div>
      )}

      <div className="mt-3">
        {item.type === "yesno" && (
          <TriButtons value={value.answer} onChange={(v) => set({ answer: v })} testid={`field-${item.id}`} />
        )}
        {item.type === "select" && (
          <select data-testid={`field-${item.id}`} value={value.answer || ""} onChange={(e) => set({ answer: e.target.value })}
            className="h-11 w-full max-w-md px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base">
            {item.options.map((o, i) => <option key={i} value={o}>{o || "— Select —"}</option>)}
          </select>
        )}
        {item.type === "text" && (
          item.id === "property_address" ? (
            <AddressField
              value={value.answer || ""}
              onChange={(v, meta) => set({ answer: v, ...(meta ? { location_meta: meta } : {}) })}
              testid={`field-${item.id}`}
              coordsPreview={value.location_meta}
            />
          ) : (
            <input data-testid={`field-${item.id}`} value={value.answer || ""} onChange={(e) => set({ answer: e.target.value })}
              className="h-11 w-full max-w-md px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"/>
          )
        )}
        {item.type === "datetime" && (
          <input data-testid={`field-${item.id}`} type="datetime-local"
            value={toInput(value.answer, "datetime")}
            onChange={(e) => set({ answer: fromInput(e.target.value, "datetime") })}
            className="h-11 px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"/>
        )}
        {item.type === "textarea" && (
          <textarea data-testid={`field-${item.id}`} rows={5} value={value.answer || ""} onChange={(e) => set({ answer: e.target.value })}
            className="w-full p-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"/>
        )}
        {item.type === "multiselect" && (
          <div className="flex flex-wrap gap-2">
            {item.options.map((o) => {
              const arr = (value.answer || "").split(",").map(s => s.trim()).filter(Boolean);
              const on = arr.includes(o);
              return (
                <button key={o} type="button" data-testid={`field-${item.id}-${o.replace(/\W+/g,'').toLowerCase()}`}
                  onClick={() => {
                    const next = on ? arr.filter(x => x !== o) : [...arr, o];
                    set({ answer: next.join(", ") });
                  }}
                  className={`h-10 px-3 text-sm font-bold border-2 ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-900 border-slate-300 hover:border-slate-900"}`}>
                  {o}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {item.notes && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1 gap-2">
            <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Notes</span>
            <VoiceToNotes
              inspectionId={inspectionId}
              currentNotes={value.notes || ""}
              onAppend={(text) => set({ notes: text })}
              testid={`field-${item.id}-notes-dictate`}
            />
          </div>
          <textarea data-testid={`field-${item.id}-notes`} rows={5} value={value.notes || ""} onChange={(e) => set({ notes: e.target.value })}
            placeholder="Optional observation… (or tap 'Dictate' to speak)"
            className="w-full p-2 text-sm text-slate-900 placeholder:text-slate-400 caret-slate-900 border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1E1B4B] focus:outline-none"
            style={{ color: "#0f172a", WebkitTextFillColor: "#0f172a" }}/>
        </div>
      )}
      {item.photos && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
            <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Photo evidence</span>
            {(value.photos || []).length > 0 ? (
              <AIPhotoCheck
                inspectionId={inspectionId}
                photoBase64={value.photos[0]}
                questionId={item.id}
                questionLabel={item.label}
                onApplyToNotes={(text) => set({ notes: (value.notes ? value.notes + "\n\n" : "") + text })}
              />
            ) : (
              <span className="text-[10px] text-slate-400 italic" data-testid={`ai-check-hint-${item.id}`}>Upload a photo to unlock AI compliance analysis</span>
            )}
          </div>
          <PhotoStrip photos={value.photos || []} onChange={(ph) => set({ photos: ph })} testidPrefix={`field-${item.id}`} backupLabel={item.label} inspectionId={inspectionId} itemId={item.id} />
        </div>
      )}

      {item.compliance && value.answer === "" && <div className="mt-2"><TriStateChip value="" testid={`status-${item.id}`} /></div>}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid={`refs-lightbox-${item.id}`}
        >
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[90vw] object-contain shadow-2xl border-2 border-white" />
        </div>
      )}
    </div>
  );
});

const TriButtons = ({ value, onChange, testid }) => (
  <div className="flex gap-2 flex-wrap">
    {[
      { v: "yes", label: "Yes", cls: "border-emerald-400 text-emerald-900", active: "bg-emerald-600 border-emerald-600 text-white" },
      { v: "no", label: "No", cls: "border-red-400 text-red-900", active: "bg-red-600 border-red-600 text-white" },
      { v: "na", label: "N/A", cls: "border-slate-300 text-slate-700", active: "bg-[#00F2C3] border-[#00F2C3] !text-slate-900" },
    ].map((opt) => (
      <button key={opt.v} type="button" data-testid={`${testid}-${opt.v}`} onClick={() => onChange(opt.v)}
        style={value === opt.v && opt.v === "na" ? { color: "#0f172a" } : undefined}
        className={`h-11 px-5 font-bold text-sm uppercase tracking-wide border-2 transition-colors ${value === opt.v ? opt.active : `bg-white ${opt.cls} hover:bg-slate-50`}`}>
        {value === opt.v ? "✓ " : ""}{opt.label}
      </button>
    ))}
  </div>
);

export default InspectionDetail;

const ShareDialog = ({ inspectionId, open, onClose, existingToken, onUpgradeNeeded }) => {
  const [token, setToken] = useState(existingToken || null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setToken(existingToken || null); }, [existingToken]);
  if (!open) return null;

  const portalUrl = token ? `${window.location.origin}/portal/${token}` : "";

  const issue = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/inspections/${inspectionId}/portal/issue`);
      setToken(r.data.portal_token);
      toast.success("Share link ready");
    } catch (e) {
      if (e?.response?.status === 402) {
        onClose();
        onUpgradeNeeded?.(e?.response?.data?.detail || "");
      } else {
        toast.error(e?.response?.data?.detail || "Failed");
      }
    }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke the link? Anyone who has it will no longer be able to view the report.")) return;
    setBusy(true);
    try {
      await api.post(`/inspections/${inspectionId}/portal/revoke`);
      setToken(null);
      toast.success("Link revoked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const copy = () => {
    navigator.clipboard.writeText(portalUrl).then(
      () => toast.success("Link copied"),
      () => toast.error("Copy failed — select the text manually")
    );
  };

  return (
    <div data-testid="share-overlay" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-slate-900 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b-2 border-slate-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Share with the homeowner</p>
            <h2 className="text-lg font-black text-slate-900">Customer portal link</h2>
          </div>
          <button onClick={onClose} data-testid="share-close" className="h-9 w-9 inline-flex items-center justify-center hover:bg-slate-100"><XIcon size={16}/></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-slate-600">A long, unguessable link the homeowner can open from any phone — no app install, no login. Shows the report, photos and your contact card. Revoke any time.</p>
          {token ? (
            <>
              <input data-testid="share-portal-url" readOnly value={portalUrl}
                onFocus={(e) => e.target.select()}
                className="w-full h-11 px-3 text-xs font-mono border-2 border-slate-300 bg-slate-50"/>
              <div className="flex flex-wrap gap-2">
                <button onClick={copy} data-testid="share-copy" className="h-11 px-4 inline-flex items-center gap-2 bg-[#1E1B4B] border-2 border-[#1E1B4B] text-white font-bold text-sm hover:bg-[#0F0E2E]">
                  <CopyIcon size={14} strokeWidth={2.5}/>Copy link
                </button>
                <a href={portalUrl} target="_blank" rel="noopener noreferrer" data-testid="share-open"
                  className="h-11 px-4 inline-flex items-center gap-2 bg-white border-2 border-slate-900 text-slate-900 font-bold text-sm hover:bg-slate-100">
                  Open preview
                </a>
                <button onClick={revoke} disabled={busy} data-testid="share-revoke"
                  className="h-11 px-4 inline-flex items-center gap-2 bg-white border-2 border-red-300 text-red-700 font-bold text-sm hover:bg-red-50 ml-auto disabled:opacity-50">
                  {busy ? <Loader2 size={14} className="animate-spin"/> : null}Revoke link
                </button>
              </div>
            </>
          ) : (
            <button onClick={issue} disabled={busy} data-testid="share-issue"
              className="h-12 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 border-2 border-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin"/> : <Share2 size={16} strokeWidth={2.5}/>}
              Create share link
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


const CORRECTION_REASONS = [
  "Status selected accidentally",
  "Incorrect inspection outcome selected",
  "Certificate was not actually issued",
  "Data entry error",
  "Other",
];

const CorrectStatusModal = ({ inspectionId, address, onClose, onDone }) => {
  const [issuedExternally, setIssuedExternally] = useState(null); // null | false | true
  const [reason, setReason] = useState(CORRECTION_REASONS[0]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (issuedExternally === null) return;
    if (reason === "Other" && !notes.trim()) { toast.error("Please add notes for 'Other'."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/inspections/${inspectionId}/correct-compliance-status`, {
        certificate_actually_issued: issuedExternally,
        reason,
        notes: notes.trim() || null,
      });
      onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not correct status", { duration: 9000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="correct-status-modal">
      <div className="bg-white max-w-lg w-full border-2 border-slate-200 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 bg-amber-500">
          <h3 className="text-sm font-black uppercase tracking-wide text-white flex items-center gap-2"><RotateCcw size={16} /> Correct Inspection Status</h3>
          <button onClick={onClose} className="text-white/90 hover:text-white"><XIcon size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[10px] font-black uppercase text-slate-400">Current status</div><div className="font-bold text-slate-800">Compliance Certificate Issued</div></div>
            <div><div className="text-[10px] font-black uppercase text-slate-400">Return to</div><div className="font-bold text-slate-800">Non-Compliant / Statutory workflow</div></div>
          </div>
          {address && <div className="bg-slate-50 border-2 border-slate-100 p-2.5 text-sm"><span className="text-[10px] font-black uppercase text-slate-400 mr-2">Property</span><span className="font-bold text-slate-800">{address}</span></div>}

          <div>
            <p className="text-sm font-bold text-slate-800 mb-2">Was the Compliance Certificate actually issued externally?</p>
            <div className="grid grid-cols-1 gap-2">
              <button onClick={() => setIssuedExternally(false)} data-testid="correct-status-no"
                className={`text-left p-3 border-2 text-sm font-bold ${issuedExternally === false ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
                NO — the status was selected accidentally
              </button>
              <button onClick={() => setIssuedExternally(true)} data-testid="correct-status-yes"
                className={`text-left p-3 border-2 text-sm font-bold ${issuedExternally === true ? "border-red-500 bg-red-50" : "border-slate-200 hover:border-slate-300"}`}>
                YES — a certificate was genuinely issued
              </button>
            </div>
          </div>

          {issuedExternally === true && (
            <div className="border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="correct-status-blocked-warning">
              This certificate has been recorded as actually issued. Reversing an issued statutory document can't be done here — it requires a separate correction/revocation process. The issued record will be kept intact.
            </div>
          )}

          {issuedExternally === false && (
            <div className="space-y-3" data-testid="correct-status-reason-block">
              <div className="border-2 border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                This will restore the previous statutory workflow and continue all deadlines from their original dates. <b>No statutory deadline will be reset.</b>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Reason for correction</label>
                <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="correct-status-reason" className="w-full h-10 px-3 border-2 border-slate-200 focus:border-amber-500 outline-none text-sm">
                  {CORRECTION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              {reason === "Other" && (
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="correct-status-notes" placeholder="Please describe what happened" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-amber-500 outline-none text-sm" />
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={onClose} className="h-10 px-4 border-2 border-slate-200 font-bold text-sm hover:border-slate-900">Cancel</button>
            {issuedExternally === false && (
              <button onClick={confirm} disabled={busy} data-testid="correct-status-confirm" className="h-10 px-4 inline-flex items-center gap-1.5 bg-amber-500 text-white font-black text-sm disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={15} />} Confirm correction</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
